import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, ExternalLink, Loader2, RefreshCw, Link2, Unlink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Stripe Connect onboarding card. Lives at the top of Settings → Payments tab.
 * Lets a contractor connect their own Stripe account so they receive funds directly.
 */
export default function StripeConnectCard({ orgId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (orgId) loadStatus();
    // Handle return from OAuth: ?stripe=connected
    const url = new URL(window.location.href);
    const flag = url.searchParams.get('stripe');
    if (flag === 'connected') {
      toast.success('Stripe account connected!');
      url.searchParams.delete('stripe');
      url.searchParams.delete('tab');
      window.history.replaceState({}, '', url.toString());
    } else if (flag === 'error') {
      toast.error(`Stripe connection failed: ${url.searchParams.get('msg') || 'unknown error'}`);
      url.searchParams.delete('stripe');
      url.searchParams.delete('msg');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/stripe/connect/status?org_id=${orgId}`);
      const data = await r.json();
      setStatus(data);
    } catch (e) {
      console.error('Failed to load Stripe status:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setBusy(true);
    try {
      const returnUrl = `${window.location.origin}/settings?tab=payments`;
      const r = await fetch(
        `${API_URL}/api/stripe/connect/authorize?org_id=${orgId}&return_url=${encodeURIComponent(returnUrl)}`
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Failed to start Stripe Connect');
        return;
      }
      window.location.href = data.oauth_url;
    } catch (e) {
      toast.error('Could not start Stripe connection');
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/stripe/connect/refresh?org_id=${orgId}`, { method: 'POST' });
      if (r.ok) {
        toast.success('Stripe status refreshed');
        await loadStatus();
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Failed to refresh');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Stripe account? You will not be able to accept online payments until you reconnect.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/stripe/connect/disconnect?org_id=${orgId}`, { method: 'POST' });
      if (r.ok) {
        toast.success('Stripe disconnected');
        await loadStatus();
      } else {
        toast.error('Failed to disconnect');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="card-default flex items-center gap-3 text-sm text-gray-500" data-testid="stripe-connect-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking Stripe status…
      </div>
    );
  }

  if (!status?.configured) {
    return (
      <div className="card-default border-amber-200 bg-amber-50/40" data-testid="stripe-not-configured">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 mb-1">Stripe is not configured on this platform yet</h3>
            <p className="text-sm text-amber-800">
              Online payments via Stripe Connect aren't available because the platform admin hasn't set
              <code className="mx-1 px-1.5 py-0.5 bg-amber-100 rounded text-xs">STRIPE_API_KEY</code> and
              <code className="mx-1 px-1.5 py-0.5 bg-amber-100 rounded text-xs">STRIPE_CONNECT_CLIENT_ID</code>.
              Please contact support to enable card payments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const connected = !!status.connected;
  const ready = connected && status.charges_enabled;

  return (
    <div className="card-default" data-testid="stripe-connect-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ready ? 'bg-green-100' : connected ? 'bg-amber-100' : 'bg-gray-100'}`}>
            {ready ? <CheckCircle2 className="w-5 h-5 text-green-700" />
              : connected ? <AlertCircle className="w-5 h-5 text-amber-700" />
              : <Link2 className="w-5 h-5 text-gray-500" />}
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold">Stripe Payments</h2>
            <p className="text-sm text-muted-foreground">
              {ready ? 'Connected · ready to accept payments'
                : connected ? 'Connected · finish onboarding to accept payments'
                : 'Connect your Stripe account to accept card payments online'}
            </p>
          </div>
        </div>
      </div>

      {!connected && (
        <>
          <div className="text-sm text-gray-600 space-y-2 mb-4">
            <p>
              When you connect Stripe, deposit and final-balance payments from your customers settle <strong>directly into your bank account</strong>.
              Funds never touch our platform.
            </p>
            <ul className="text-xs text-gray-500 list-disc list-inside space-y-0.5">
              <li>Standard Stripe fees apply (2.9% + $0.30 per transaction in the US)</li>
              <li>You own the account, disputes, payouts, and tax documents</li>
              <li>You can disconnect at any time</li>
            </ul>
          </div>
          <Button
            onClick={handleConnect}
            disabled={busy}
            className="bg-[#635bff] hover:bg-[#5045e5] text-white"
            data-testid="connect-stripe-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
            Connect with Stripe
            <ExternalLink className="w-3.5 h-3.5 ml-2 opacity-70" />
          </Button>
        </>
      )}

      {connected && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Account</p>
              <p className="font-mono text-xs text-gray-700 truncate" data-testid="stripe-account-id">
                {status.account_id}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Status</p>
              <div className="flex flex-wrap gap-1.5">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${status.charges_enabled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {status.charges_enabled ? 'Charges enabled' : 'Charges disabled'}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${status.payouts_enabled ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {status.payouts_enabled ? 'Payouts enabled' : 'Payouts disabled'}
                </span>
              </div>
            </div>
          </div>

          {!status.charges_enabled && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <strong>Finish your Stripe onboarding</strong> to start accepting payments. Stripe may need additional business info, your bank account, or identity verification.
              {' '}
              <a
                href="https://dashboard.stripe.com/account/onboarding"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Continue on Stripe →
              </a>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={busy} data-testid="stripe-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
              Refresh status
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={busy} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="stripe-disconnect-btn">
              <Unlink className="w-3.5 h-3.5 mr-1.5" />
              Disconnect
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
